import { getApiMethod } from '@/modules/apiSelector'
import { COUNTRY_ID, BIZMATES_TIMEZONE_STUDENT, BIZMATES_TIMEZONE_TRAINER, ZIPAN_TIMEZONE_TRAINER } from '@/utils/constants';
import logger from '@/modules/Logger';
import { store as localStorage } from '@/utils/common';
import { SET_MYSTAGE_THEME, SET_HIDE_MYSTAGE_RECORD_MESSAGE } from '@/graphql/mutations/userSetting';
import { apolloProvider } from '@/modules/myStageGraphQL';

/**
 * Syncs theme from localStorage to DB for first-time users.
 * Fire-and-forget: logs success/failure but never blocks.
 */
function syncThemeToDb(themeColor) {
  const themeValue = themeColor === 'dark' ? 2 : 1;
  logger.info('[UserOtherSetting] First-time sync: theme to DB', {
    operation: 'user:setting:sync',
    value: themeColor
  });
  apolloProvider.defaultClient.mutate({
    mutation: SET_MYSTAGE_THEME,
    variables: { color: themeValue }
  }).catch((error) => {
    logger.error('[UserOtherSetting] Failed to sync theme to DB', {
      operation: 'user:setting:sync',
      error: error.message
    });
  });
}

/**
 * Syncs hide record message from localStorage to DB for first-time users.
 * Fire-and-forget: logs success/failure but never blocks.
 */
function syncHideRecordMessageToDb(hideValue) {
  const intValue = hideValue ? 1 : 0;
  logger.info('[UserOtherSetting] First-time sync: hide record message to DB', {
    operation: 'user:setting:sync',
    value: hideValue
  });
  apolloProvider.defaultClient.mutate({
    mutation: SET_HIDE_MYSTAGE_RECORD_MESSAGE,
    variables: { value: intValue }
  }).catch((error) => {
    logger.error('[UserOtherSetting] Failed to sync hide record message to DB', {
      operation: 'user:setting:sync',
      error: error.message
    });
  });
}

export default {
  namespaced: true,
  state: {
    userTimezone: BIZMATES_TIMEZONE_STUDENT,
    language: 'ja',
    countryId: null,
    remoteStudentCountryId: null,
    isHideRecordMessage: false
  },
  getters: {
    isTaiwanCountryId: state => state.countryId === COUNTRY_ID.taiwan,

    isJapanCountryId: state => state.countryId === COUNTRY_ID.japan,

    isRemoteStudentTaiwan: state => state.remoteStudentCountryId === COUNTRY_ID.taiwan,

    targetLanguage: (state, getters, rootState, rootGetters) => {
      if (getters.isTaiwanCountryId || getters.isRemoteStudentTaiwan) {
        return 'en';
      }

      // Zipan service logic
      if (rootGetters.isZipanService) {
        const savedLang = localStorage.getLang();
        return savedLang ?? (rootGetters.isStudentGlobalState ? 'en' : 'ja');
      }

      return rootGetters.isStudentGlobalState ? 'ja' : 'en';
    },
    trainerTimeZone: (state, getters, rootState, rootGetters) => {
      return rootGetters.isZipanService ? ZIPAN_TIMEZONE_TRAINER : BIZMATES_TIMEZONE_TRAINER;
    },
  },
  mutations: {
    setUserTimezone(state, payload) {
      state.userTimezone = payload ?? BIZMATES_TIMEZONE_STUDENT;
    },
    setLanguage(state, lang) {
      state.language = lang;
    },
    setUserCountryId(state, countryId) {
      state.countryId = countryId;
    },
    setRemoteStudentCountryId(state, countryId) {
      state.remoteStudentCountryId = countryId;
    },
    setIsHideRecordMessage(state, payload) {
      state.isHideRecordMessage = (typeof payload === 'boolean') ? payload : false;
    }
  },
  actions: {
    async getUserOtherSetting({ commit, rootState, rootGetters, getters }, payload = {}) {
      const { routeName } = payload;
      const isLessonRoute = routeName === 'Lesson';

      const userSettingApi = getApiMethod('getUserOtherSetting');

      if (typeof userSettingApi !== 'function') {
        return;
      }

      try {
        const setting = await userSettingApi();
        commit('setUserTimezone', rootGetters.isTestLesson ? getters.trainerTimeZone : setting.user_timezone);
        commit('setUserCountryId', setting.country_id);

        // role_code for non canary student is not present
        // but actualAcctType will be fetched using getBetaAccess so no need to set it
        if (setting.role_code) {
          commit('setActualAccType', setting.role_code, { root: true });
        }

        // Process mystage_theme from API response
        const themeMap = { 1: 'light', 2: 'dark' };
        if (setting.mystage_theme != null && themeMap[setting.mystage_theme]) {
          const themeColor = themeMap[setting.mystage_theme];
          commit('layout/setLayoutColor', themeColor, { root: true });
          localStorage.setColor(themeColor);
        } else {
          const themeColor = localStorage.getColor(null);
          commit('layout/setLayoutColor', themeColor || 'light', { root: true });
          if (isLessonRoute && themeColor) {
            syncThemeToDb(themeColor);
          }
        }

        // Process is_hide_mystage_record_message from API response
        if (setting.is_hide_mystage_record_message != null) {
          commit('setIsHideRecordMessage', setting.is_hide_mystage_record_message === 1);
          localStorage.setHideRecordMessage(rootState.userId, setting.is_hide_mystage_record_message === 1);
        } else {
          const fallbackHideMessage = localStorage.getHideRecordMessage(rootState.userId) || false;
          commit('setIsHideRecordMessage', fallbackHideMessage);
          if (isLessonRoute && fallbackHideMessage) {
            syncHideRecordMessageToDb(fallbackHideMessage);
          }
        }
      } catch (error) {
        // Fallback to localStorage for theme
        const fallbackTheme = localStorage.getColor('light');
        commit('layout/setLayoutColor', fallbackTheme, { root: true });

        // Fallback to localStorage for hide record message
        const fallbackHideMessage = localStorage.getHideRecordMessage(rootState.userId) || false;
        commit('setIsHideRecordMessage', fallbackHideMessage);

        logger.error('Failed to retrieve user other setting: ' + error.message, {
          operation: 'user:setting'
        });
      }
    }
  }
};
